// G+J PDP redesign — star rating + review count from Yotpo's public widget API.
(() => {
  const loadBottomline = (el) => {
    const { appKey, productId } = el.dataset;
    if (!appKey || !productId || el.dataset.loaded) return;
    el.dataset.loaded = 'true';

    // v3 storefront API: includes Shop app reviews (v1 under-counted).
    fetch(`https://api-cdn.yotpo.com/v3/storefront/store/${appKey}/product/${productId}/reviews?page=1&perPage=1`)
      .then((response) => response.json())
      .then((data) => {
        const { averageScore: average, totalReview: total } = data?.bottomline || {};
        if (!total) return;
        el.querySelector('.gj-stars')?.style.setProperty('--rating', average);
        el.querySelector('[data-count]').textContent = `${total} ${total === 1 ? 'Review' : 'Reviews'}`;
        el.setAttribute('aria-label', `${Number(average).toFixed(1)} out of 5 stars, ${total} reviews`);
        el.hidden = false;
      })
      .catch(() => {});
  };

  const init = (root = document) => root.querySelectorAll('[data-yotpo-bottomline]').forEach(loadBottomline);

  init();

  // Smooth-scroll in-page links (stars → reviews, pill → Complete the look); respects reduced motion.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a.gj-pdp__stars[href^="#"], a.gj-pdp__pill[href^="#"]');
    const target = link && document.querySelector(link.getAttribute('href'));
    if (!target) return;
    event.preventDefault();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', link.getAttribute('href'));
  });

  // Re-run when Dawn swaps product markup (e.g. after a variant change).
  document.addEventListener('product-info:loaded', (event) => init(event.target));
})();

// Mobile gallery carousel: one image per slide with bullet navigation.
if (!customElements.get('gj-gallery')) {
  customElements.define(
    'gj-gallery',
    class GjGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-gallery-track]');
        this.dots = this.querySelector('[data-gallery-dots]');
        this.slides = Array.from(this.track.children);
        if (this.slides.length < 2) return;

        this.dots.innerHTML = this.slides
          .map((_, i) => `<button type="button" aria-label="Go to image ${i + 1}"></button>`)
          .join('');
        this.buttons = Array.from(this.dots.children);
        this.buttons.forEach((button, i) =>
          button.addEventListener('click', () =>
            this.track.scrollTo({ left: this.slides[i].offsetLeft - this.track.offsetLeft, behavior: 'smooth' })
          )
        );

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
        this.setActive(0);
        this.initMouseDrag();
      }

      // Touch and trackpads scroll natively; mouse needs click-and-drag added.
      initMouseDrag() {
        let startX = 0;
        let startScroll = 0;
        let dragging = false;
        let moved = false;

        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          if (getComputedStyle(this.track).overflowX === 'visible') return; // desktop grid
          dragging = true;
          moved = false;
          startX = event.clientX;
          startScroll = this.track.scrollLeft;
          this.track.classList.add('is-dragging');
        });

        window.addEventListener('pointermove', (event) => {
          if (!dragging) return;
          const delta = event.clientX - startX;
          if (Math.abs(delta) > 3) moved = true;
          this.track.scrollLeft = startScroll - delta;
        });

        window.addEventListener('pointerup', (event) => {
          if (!dragging) return;
          dragging = false;

          const delta = event.clientX - startX;
          const width = this.track.clientWidth;
          let index = Math.round(startScroll / width);
          if (Math.abs(delta) > width * 0.15) index += delta < 0 ? 1 : -1;
          index = Math.max(0, Math.min(this.slides.length - 1, index));
          this.track.scrollTo({ left: this.slides[index].offsetLeft - this.track.offsetLeft, behavior: 'smooth' });

          // Re-enable snapping only after the smooth scroll settles; turning it back on
          // mid-animation makes the browser snap and our scroll fight (visible jump).
          const restoreSnap = () => {
            clearTimeout(fallback);
            this.track.removeEventListener('scrollend', restoreSnap);
            if (!dragging) this.track.classList.remove('is-dragging');
          };
          const fallback = setTimeout(restoreSnap, 700);
          this.track.addEventListener('scrollend', restoreSnap);
        });

        // Don't follow links (e.g. the pill) or start native image drags after a drag.
        this.track.addEventListener('click', (event) => moved && event.preventDefault(), true);
        this.track.addEventListener('dragstart', (event) => event.preventDefault());
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      setActive(index) {
        this.buttons.forEach((button, i) => button.setAttribute('aria-current', i === index ? 'true' : 'false'));
      }
    }
  );
}

// Sticky info column that can be taller than the viewport: stick to the top when it fits,
// otherwise stick by its bottom edge so everything in it (Complete the look, story) stays reachable.
(() => {
  const GAP = 24;
  const update = (info) => {
    const overflow = info.offsetHeight + GAP * 2 - window.innerHeight;
    info.style.setProperty('--gj-sticky-top', `${overflow > 0 ? GAP - overflow : GAP}px`);
  };
  const init = () => {
    document.querySelectorAll('.gj-pdp__info').forEach((info) => {
      if (info.dataset.stickyInit) return;
      info.dataset.stickyInit = 'true';
      new ResizeObserver(() => update(info)).observe(info);
      window.addEventListener('resize', () => update(info));
      update(info);
    });
  };
  init();
  document.addEventListener('product-info:loaded', init);
})();

// Sticky add to cart bar. Mirrors and drives the main form rather than duplicating it.
if (!customElements.get('gj-sticky-atc')) {
  customElements.define(
    'gj-sticky-atc',
    class GjStickyAtc extends HTMLElement {
      connectedCallback() {
        this.section = document.getElementById(`MainProduct-${this.dataset.section}`);
        this.buyRow = this.section?.querySelector('.gj-pdp__buy');
        if (!this.section || !this.buyRow) return;

        this.sizeSelect = this.querySelector('[data-size-select]');
        this.colorSelect = this.querySelector('[data-color-select]');
        this.submitButton = this.querySelector('[data-sticky-submit]');

        // Show as soon as any part of the main buy row has scrolled above the viewport.
        // A scroll check (not IntersectionObserver) so fast flings / jumps past it still count.
        let ticking = false;
        this.onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            const past = this.buyRow.getBoundingClientRect().top < 0;
            if (past === this.classList.contains('is-visible')) return;
            this.hidden = false;
            this.classList.toggle('is-visible', past);
            this.setAttribute('aria-hidden', String(!past));
            this.toggleAttribute('inert', !past);
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.hidden = false;
        this.setAttribute('aria-hidden', 'true');
        this.toggleAttribute('inert', true);
        this.onScroll();

        // Each color is its own product, so this loads a new page; keep the shopper's place on it.
        this.colorSelect?.addEventListener('change', () => {
          try {
            sessionStorage.setItem('gj-restore-scroll', String(window.scrollY));
          } catch (e) {}
          window.location.href = this.colorSelect.value;
        });

        this.sizeSelect?.addEventListener('change', () => {
          const position = this.sizeSelect.dataset.optionPosition;
          const radio = Array.from(this.section.querySelectorAll(`variant-selects input[name^="${CSS.escape(this.optionName)}-${position}"]`)).find(
            (input) => input.value === this.sizeSelect.value
          );
          if (radio && !radio.checked) {
            this.holdScroll();
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.updateValueLabels();
        });

        this.submitButton.addEventListener('click', () => {
          const form = document.getElementById(this.dataset.formId);
          if (form && !this.mainButton?.disabled) form.requestSubmit();
        });


        // Keep in sync with the main form (size buttons, price, sold out state).
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => this.sync());
        this.sync();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.unsubscribe?.();
      }

      // Dawn refocuses the clicked size radio after re-rendering, which scrolls the page up to the
      // main form. When the change came from this bar, undo that jump and keep focus here.
      holdScroll() {
        const y = window.scrollY;
        const onFocus = (event) => {
          if (!this.section.contains(event.target)) return;
          window.scrollTo(0, y);
          this.sizeSelect.focus({ preventScroll: true });
        };
        document.addEventListener('focusin', onFocus);
        setTimeout(() => document.removeEventListener('focusin', onFocus), 4000);
      }

      get optionName() {
        return this.sizeSelect.getAttribute('aria-label');
      }

      get mainButton() {
        return document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
      }

      sync() {
        const checked = this.sizeSelect && this.section.querySelector(`.gj-sizes input:checked`);
        if (checked) this.sizeSelect.value = checked.value;

        // Mark sold-out sizes from the main buttons (Dawn re-renders them on change).
        this.sizeSelect?.querySelectorAll('option').forEach((option) => {
          const input = Array.from(this.section.querySelectorAll('.gj-sizes input')).find((i) => i.value === option.value);
          if (!input) return;
          const soldOut = input.classList.contains('disabled');
          option.textContent = soldOut ? `${option.value} – ${window.variantStrings?.soldOut || 'Sold out'}` : option.value;
        });

        const mainPrice = document.getElementById(`price-${this.dataset.section}`);
        const price = this.querySelector('[data-sticky-price]');
        if (mainPrice && price) price.innerHTML = mainPrice.innerHTML;

        const main = this.mainButton;
        if (main) {
          this.submitButton.disabled = main.disabled;
          this.submitButton.textContent = main.querySelector('span')?.textContent.trim() || this.submitButton.textContent;
        }
        this.updateValueLabels();
      }

      updateValueLabels() {
        this.querySelectorAll('.gj-sticky__select').forEach((label) => {
          const select = label.querySelector('select');
          const value = label.querySelector('[data-value]');
          if (select && value) value.textContent = select.value.startsWith('/') ? select.selectedOptions[0].textContent.split(' – ')[0].trim() : select.value;
        });
      }

    }
  );
}

// Restore scroll position after switching color from the sticky bar (new product page).
(() => {
  try {
    const y = sessionStorage.getItem('gj-restore-scroll');
    if (y === null) return;
    sessionStorage.removeItem('gj-restore-scroll');
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    const restore = () => window.scrollTo(0, parseInt(y, 10));
    restore();
    window.addEventListener('load', restore, { once: true });
  } catch (e) {}
})();

// Dawn re-renders the size buttons after a change and calls .focus() on the clicked one without
// preventScroll. Inside our sticky info column that makes browsers scroll to the column's static
// position (top of the page). Hold the scroll position for any size change on the PDP.
(() => {
  document.addEventListener(
    'change',
    (event) => {
      const selects = event.target.closest?.('.gj-pdp variant-selects');
      if (!selects) return;
      const y = window.scrollY;
      const onFocus = (focusEvent) => {
        if (!focusEvent.target.closest?.('.gj-pdp variant-selects')) return;
        if (Math.abs(window.scrollY - y) > 1) window.scrollTo(0, y);
      };
      document.addEventListener('focusin', onFocus);
      setTimeout(() => document.removeEventListener('focusin', onFocus), 4000);
    },
    true
  );
})();

// Quick view drawer (Figma 28:94). Content is the gj-quick-view section rendered for the
// clicked product via the Section Rendering API, so Dawn's product-info/product-form handle
// size changes and add to cart inside it.
if (!customElements.get('gj-quick-view')) {
  customElements.define(
    'gj-quick-view',
    class GjQuickView extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('.gj-qv-drawer__panel');
        this.content = this.querySelector('[data-qv-content]');
        this.loading = this.querySelector('[data-qv-loading]');

        document.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-quick-view]');
          if (!trigger) return;
          event.preventDefault();
          this.opener = trigger;
          this.open(trigger.dataset.quickView);
        });

        this.querySelectorAll('[data-qv-close]').forEach((button) => button.addEventListener('click', () => this.close()));
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.close();
          if (event.key === 'Tab') this.trapFocus(event);
        });

        // Colors are separate products: load the sibling into the drawer instead of leaving.
        this.content.addEventListener('click', (event) => {
          const swatch = event.target.closest('a.gj-swatch');
          if (!swatch) return;
          event.preventDefault();
          this.load(swatch.getAttribute('href'));
        });

        // Close once something from the drawer lands in the cart (cart notification takes over).
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (this.classList.contains('is-open') && event?.source === 'product-form' && this.content.querySelector('#product-form-gj-quick-view')) {
            this.close({ restoreFocus: false });
          }
        });
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      open(url) {
        this.hidden = false;
        document.documentElement.classList.add('gj-qv-lock');
        requestAnimationFrame(() => this.classList.add('is-open'));
        this.load(url).then(() => this.panel.focus({ preventScroll: true }));
      }

      load(url) {
        this.loading.hidden = false;
        this.content.setAttribute('aria-busy', 'true');
        const path = url.split('?')[0];
        return fetch(`${path}?section_id=gj-quick-view`)
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            const section = html.querySelector('.gj-qv-section') || html.body;
            this.content.replaceChildren(...section.childNodes);
            this.panel.scrollTop = 0;
            window.Shopify?.PaymentButton?.init?.();
          })
          .catch(() => {
            const message = document.createElement('p');
            message.className = 'gj-qv__error';
            message.innerHTML = `Sorry, this product couldn't be loaded. <a href="${encodeURI(path)}">View product</a>`;
            this.content.replaceChildren(message);
          })
          .finally(() => {
            this.loading.hidden = true;
            this.content.removeAttribute('aria-busy');
          });
      }

      close({ restoreFocus = true } = {}) {
        if (this.hidden) return;
        this.classList.remove('is-open');
        document.documentElement.classList.remove('gj-qv-lock');
        const done = () => {
          if (this.classList.contains('is-open')) return;
          this.hidden = true;
          this.content.replaceChildren();
        };
        this.panel.addEventListener('transitionend', done, { once: true });
        setTimeout(done, 450);
        if (restoreFocus) this.opener?.focus({ preventScroll: true });
      }

      trapFocus(event) {
        const focusable = Array.from(
          this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, summary, [tabindex]:not([tabindex="-1"])')
        ).filter((el) => el.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}

// Full-screen gallery viewer (Figma 45:461). One image at a time at full width, scroll to pan;
// thumbnails switch images; arrow keys / swipe step through; Esc closes.
if (!customElements.get('gj-gallery-viewer')) {
  customElements.define(
    'gj-gallery-viewer',
    class GjGalleryViewer extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('.gj-gv__dialog');
        this.scroller = this.querySelector('[data-gv-scroll]');
        this.images = Array.from(this.querySelectorAll('[data-gv-image]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-gv-thumb]'));
        if (!this.images.length) return;

        document.addEventListener('click', (event) => {
          const tile = event.target.closest('[data-gv-open]');
          if (!tile || event.target.closest('a, button')) return;
          if (event.defaultPrevented) return; // e.g. the mobile carousel just finished a drag
          const index = this.images.findIndex((image) => image.dataset.mediaId === tile.dataset.gvOpen);
          this.opener = tile;
          this.open(Math.max(0, index));
        });

        this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.show(parseInt(thumb.dataset.gvThumb, 10))));
        this.querySelector('[data-gv-close]').addEventListener('click', () => this.close());
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.close();
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown' && event.altKey) this.step(1);
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp' && event.altKey) this.step(-1);
        });

        // Mouse: click and drag to move around the image (touch already scrolls natively).
        let drag = null;
        this.scroller.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          drag = { x: event.clientX, y: event.clientY, left: this.scroller.scrollLeft, top: this.scroller.scrollTop };
          this.scroller.classList.add('is-dragging');
          this.scroller.setPointerCapture(event.pointerId);
        });
        this.scroller.addEventListener('pointermove', (event) => {
          if (!drag) return;
          this.scroller.scrollLeft = drag.left - (event.clientX - drag.x);
          this.scroller.scrollTop = drag.top - (event.clientY - drag.y);
        });
        const endDrag = () => {
          drag = null;
          this.scroller.classList.remove('is-dragging');
        };
        this.scroller.addEventListener('pointerup', endDrag);
        this.scroller.addEventListener('pointercancel', endDrag);
        this.scroller.addEventListener('dragstart', (event) => event.preventDefault());

        // Horizontal swipe changes image; vertical scroll still pans the current one.
        let startX = 0;
        let startY = 0;
        this.scroller.addEventListener('touchstart', (event) => {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
          this.swipeStartLeft = this.scroller.scrollLeft;
        }, { passive: true });
        this.scroller.addEventListener('touchend', (event) => {
          const dx = event.changedTouches[0].clientX - startX;
          const dy = event.changedTouches[0].clientY - startY;
          if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
          // When the image is wider than the screen (mobile, full height), a swipe pans it first;
          // only change image once it was already at that edge when the swipe started.
          const maxLeft = this.scroller.scrollWidth - this.scroller.clientWidth;
          const atEdge = dx < 0 ? this.swipeStartLeft >= maxLeft - 2 : this.swipeStartLeft <= 2;
          if (maxLeft <= 2 || atEdge) this.step(dx < 0 ? 1 : -1);
        });
      }

      open(index) {
        this.hidden = false;
        document.documentElement.classList.add('gj-qv-lock');
        this.show(index);
        requestAnimationFrame(() => this.classList.add('is-open'));
        this.dialog.focus({ preventScroll: true });
      }

      show(index) {
        this.index = (index + this.images.length) % this.images.length;
        this.images.forEach((image, i) => {
          image.hidden = i !== this.index;
          // Load the neighbours early so stepping feels instant.
          if (Math.abs(i - this.index) <= 1) image.querySelector('img')?.setAttribute('loading', 'eager');
        });
        this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === this.index)));
        this.thumbs[this.index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        this.scroller.scrollTop = 0;
        this.centerImage();
      }

      // Start each image centred horizontally (matters when it's wider than the screen).
      centerImage() {
        const img = this.images[this.index]?.querySelector('img');
        const center = () => {
          this.scroller.scrollLeft = (this.scroller.scrollWidth - this.scroller.clientWidth) / 2;
        };
        if (img && !img.complete) img.addEventListener('load', center, { once: true });
        requestAnimationFrame(center);
      }

      step(direction) {
        this.show(this.index + direction);
      }

      close() {
        if (this.hidden) return;
        this.classList.remove('is-open');
        document.documentElement.classList.remove('gj-qv-lock');
        setTimeout(() => {
          if (!this.classList.contains('is-open')) this.hidden = true;
        }, 250);
        this.opener?.focus?.({ preventScroll: true });
      }
    }
  );
}

// Keyboard: Enter / Space on a gallery tile opens the viewer.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const tile = event.target.closest?.('[data-gv-open]');
  if (!tile || event.target !== tile) return;
  event.preventDefault();
  tile.click();
});
